/**
 * Automatic tennis-court calibration from a single video frame, using
 * classical CV only (no ML). See ARCHITECTURE.md §Sprint 3.
 *
 * Unlike the original "find the 4 outer corners" design, this calibrator
 * anchors the homography on the 4 service-box T-intersections, which are
 * almost always visible in broadcast framing even when the outer doubles
 * corners are cropped off-screen.
 *
 * Pipeline:
 *   1. Downsample to `inputWidth`, gray, blur, Sobel, court-color mask, Hough.
 *   2. Classify lines into horizontals (θ within ±45° of π/2) and verticals
 *      (the rest in [0, π)). Compute each line's pixel length inside the
 *      frame rectangle for scoring.
 *   3. Find the NET: the longest horizontal closest to vertical center.
 *   4. Find the 2 service lines: the horizontal pair above/below the net that
 *      is (a) symmetric and (b) at 5%..35% of frame height from the net.
 *   5. Find the 2 singles sidelines: the outermost verticals that cross all
 *      three horizontals inside the frame.
 *   6. 4 T-intersections = service lines × singles sidelines (primary anchors).
 *   7. Optional center-T and doubles corners to strengthen the fit.
 *   8. Fit homography over ALL anchors (least-squares DLT); fall back to the
 *      minimal 4-point solve on the T-intersections.
 *   9. Sanity-check (net not inverted, service lines on opposite sides of
 *      net, T-quad convex).
 *  10. Project the canonical ITF doubles corners back via H⁻¹ for overlay
 *      rendering, even when some real corners are off-frame.
 *
 * Designed to fail gracefully (`null`) — a wrong homography is worse than
 * no calibration.
 */

import {
  computeHomography,
  COURT_M,
  rgbaToGray,
  gaussianBlur5,
  sobelEdges,
  houghLines,
  lineIntersect,
  applyCourtColorMask,
  applyHomography,
  invert3x3,
  SingularHomographyError,
  type Matrix3x3,
  type Quad,
  type HoughLine,
} from '@tbc/core/court';

export interface CalibrationResult {
  /** 4 pixel corners of the DOUBLES rectangle in the ORIGINAL frame coords,
   *  order: top-left, top-right, bottom-right, bottom-left. Projected via
   *  H⁻¹ so they can still be drawn even when partially off-screen. */
  readonly corners: readonly [number, number][];
  /** Homography H mapping pixel -> court metres (ITF doubles: 23.77 × 10.97). */
  readonly homography: Matrix3x3;
  /** Heuristic confidence in [0,1]. */
  readonly confidence: number;
}

export interface AutoCourtCalibratorOptions {
  /** Target downsample width (px). Default 640. */
  readonly inputWidth?: number;
  /** Sobel edge threshold (0..255). Default 60. */
  readonly sobelThreshold?: number;
  /** Hough accumulator vote threshold. Default proportional to `inputWidth/4`. */
  readonly houghVoteThreshold?: number;
  /** θ bin size in degrees for the Hough transform. Default 1°. */
  readonly thetaStepDeg?: number;
  /** Max Hough lines to retain. Default 60. */
  readonly maxLines?: number;
}

/** ITF DOUBLES canonical quad in metres, TL, TR, BR, BL. */
const ITF_DOUBLES_QUAD: Quad = [
  [0, 0],
  [COURT_M.DOUBLES_WIDTH, 0],
  [COURT_M.DOUBLES_WIDTH, COURT_M.LENGTH],
  [0, COURT_M.LENGTH],
];

/** ITF T-intersections (singles sideline × service line) in metres.
 *  In the doubles-frame: alley = (10.97 − 8.23) / 2 = 1.37 m.
 *  Service lines are 6.40 m from the net (at y = 11.885), so they sit at
 *  5.485 m and 18.285 m.
 *  Order: TL-T, TR-T, BR-T, BL-T (mirrors ITF_DOUBLES_QUAD winding). */
const ITF_T_INTERSECTIONS: Quad = [
  [COURT_M.DOUBLES_WIDTH / 2 - COURT_M.SINGLES_WIDTH / 2, COURT_M.LENGTH / 2 - COURT_M.SERVICE_LINE],
  [COURT_M.DOUBLES_WIDTH / 2 + COURT_M.SINGLES_WIDTH / 2, COURT_M.LENGTH / 2 - COURT_M.SERVICE_LINE],
  [COURT_M.DOUBLES_WIDTH / 2 + COURT_M.SINGLES_WIDTH / 2, COURT_M.LENGTH / 2 + COURT_M.SERVICE_LINE],
  [COURT_M.DOUBLES_WIDTH / 2 - COURT_M.SINGLES_WIDTH / 2, COURT_M.LENGTH / 2 + COURT_M.SERVICE_LINE],
];

/** Accepted frame inputs. Plain ImageData accepted to ease jsdom testing. */
export type CalibratorFrame =
  | HTMLVideoElement
  | HTMLCanvasElement
  | ImageBitmap
  | ImageData;

export class AutoCourtCalibrator {
  private readonly inputWidth: number;
  private readonly sobelThreshold: number;
  private readonly houghVoteThreshold: number;
  private readonly thetaStepDeg: number;
  private readonly maxLines: number;
  private off: OffscreenCanvas | null = null;

  constructor(opts: AutoCourtCalibratorOptions = {}) {
    this.inputWidth = opts.inputWidth ?? 640;
    this.sobelThreshold = opts.sobelThreshold ?? 60;
    this.houghVoteThreshold = opts.houghVoteThreshold ?? Math.round(this.inputWidth / 4);
    this.thetaStepDeg = opts.thetaStepDeg ?? 1;
    this.maxLines = opts.maxLines ?? 60;
  }

  /** Try to detect the court. Returns null if detection is unreliable. */
  calibrate(frame: CalibratorFrame): CalibrationResult | null {
    const { rgba, width, height, origWidth, origHeight } = extractRgba(
      frame,
      this.inputWidth,
      () => this.getOffscreen(),
    );

    const gray = rgbaToGray(rgba, width, height);
    const blurred = gaussianBlur5(gray);
    const edges0 = sobelEdges(blurred, this.sobelThreshold);
    const edges = applyCourtColorMask(edges0, rgba);

    const lines = houghLines(edges, {
      voteThreshold: this.houghVoteThreshold,
      maxLines: this.maxLines,
      thetaStepDeg: this.thetaStepDeg,
      nmsRho: 8,
      nmsTheta: Math.max(2, Math.round(2 / this.thetaStepDeg)),
    });
    if (lines.length < 3) return null;

    // 1. Classify + annotate with pixel length inside the frame rectangle.
    const { horizontals, verticals } = classifyLines(lines, width, height);
    if (horizontals.length < 2 || verticals.length < 2) return null;

    // 2. Find the net.
    const net = pickNet(horizontals, width, height);
    if (!net || net.score < 0.3) return null;

    // 3. Find the 2 service lines, symmetric around the net.
    const service = pickServiceLines(horizontals, net, width, height);
    if (!service) return null;
    const { top: serviceTop, bottom: serviceBottom, score: servicePairScore } = service;

    // 4. Find candidate vertical sidelines (all verticals that cross the 3
    //    horizontals inside the frame). These include the singles AND, when
    //    visible, the doubles sidelines.
    const sidelineCandidates = pickSidelineCandidates(
      verticals,
      [serviceTop.line, net.line, serviceBottom.line],
      width,
      height,
    );
    if (sidelineCandidates.length < 2) return null;

    // 5. Disambiguate singles vs. doubles. A tennis court has a CENTER
    //    service line between the two singles sidelines, spanning ONLY the
    //    space between the service lines. If we can find that center
    //    service line, the singles are the two candidates flanking it.
    //    Otherwise we fall back to the outermost pair (what the task
    //    describes as "outermost strong verticals") and hope no doubles
    //    lines are visible.
    const singles = disambiguateSingles(
      sidelineCandidates,
      verticals,
      serviceTop.line,
      serviceBottom.line,
      net.line,
      width,
      height,
    );
    if (!singles) return null;
    const { left: leftSingle, right: rightSingle, score: sidelineScore } = singles;

    // 5. Compute the 4 T-intersections (primary anchors).
    const tTL = lineIntersect(serviceTop.line, leftSingle.line);
    const tTR = lineIntersect(serviceTop.line, rightSingle.line);
    const tBR = lineIntersect(serviceBottom.line, rightSingle.line);
    const tBL = lineIntersect(serviceBottom.line, leftSingle.line);
    if (!tTL || !tTR || !tBR || !tBL) return null;
    const tQuad: [number, number][] = [tTL, tTR, tBR, tBL];
    if (!isConvex(tQuad)) return null;
    // Sanity: service lines must be on opposite sides of the net.
    const yNet = yAtX(net.line, width / 2);
    const yTop = (tTL[1] + tTR[1]) / 2;
    const yBot = (tBL[1] + tBR[1]) / 2;
    if (!(yTop < yNet && yBot > yNet)) return null;

    // 6. Build anchor list (starting with the 4 T-intersections).
    const anchorsPx: [number, number][] = [...tQuad];
    const anchorsM: [number, number][] = [
      ITF_T_INTERSECTIONS[0],
      ITF_T_INTERSECTIONS[1],
      ITF_T_INTERSECTIONS[2],
      ITF_T_INTERSECTIONS[3],
    ] as unknown as [number, number][];

    // 7. Optional center-T intersection (vertical near x=width/2 crossing
    //    both service lines). Reuse the center line found during singles
    //    disambiguation if available; otherwise try a dedicated scan.
    const centerSl =
      singles.centerLine ??
      pickCenterServiceLine(verticals, leftSingle, rightSingle, net.line);
    if (centerSl) {
      const cTop = lineIntersect(serviceTop.line, centerSl);
      const cBot = lineIntersect(serviceBottom.line, centerSl);
      if (cTop && cBot) {
        anchorsPx.push(cTop, cBot);
        anchorsM.push(
          [COURT_M.DOUBLES_WIDTH / 2, COURT_M.LENGTH / 2 - COURT_M.SERVICE_LINE],
          [COURT_M.DOUBLES_WIDTH / 2, COURT_M.LENGTH / 2 + COURT_M.SERVICE_LINE],
        );
      }
    }

    // 8. Optional doubles sidelines (outside the singles ones).
    const doubles = pickDoublesSidelines(verticals, leftSingle, rightSingle, width, height, [
      serviceTop.line,
      net.line,
      serviceBottom.line,
    ]);
    if (doubles) {
      const { left: leftD, right: rightD } = doubles;
      const dTL = lineIntersect(serviceTop.line, leftD);
      const dTR = lineIntersect(serviceTop.line, rightD);
      const dBR = lineIntersect(serviceBottom.line, rightD);
      const dBL = lineIntersect(serviceBottom.line, leftD);
      if (dTL && dTR && dBR && dBL) {
        anchorsPx.push(dTL, dTR, dBR, dBL);
        const yServTop = COURT_M.LENGTH / 2 - COURT_M.SERVICE_LINE;
        const yServBot = COURT_M.LENGTH / 2 + COURT_M.SERVICE_LINE;
        anchorsM.push(
          [0, yServTop],
          [COURT_M.DOUBLES_WIDTH, yServTop],
          [COURT_M.DOUBLES_WIDTH, yServBot],
          [0, yServBot],
        );
      }
    }

    // 9. Fit homography. We ALWAYS use the 4 T-intersections as the primary
    //    anchors (highest-confidence, closest to frame center where Hough is
    //    least noisy) and then — if we have ≥ 1 extra anchor — refine the
    //    fit via least-squares. Refinement is skipped when it would INCREASE
    //    the residual on the 4 T-intersections, keeping behaviour safe when
    //    the extra anchors are noisy.
    let homographyDown: Matrix3x3;
    try {
      const base = computeHomography(
        (anchorsPx.slice(0, 4) as unknown) as Quad,
        (anchorsM.slice(0, 4) as unknown) as Quad,
      );
      if (anchorsPx.length > 4) {
        const refined = refineHomographyLS(anchorsPx, anchorsM, base);
        const baseErr = residual(anchorsPx.slice(0, 4), anchorsM.slice(0, 4), base);
        const refErr = residual(anchorsPx.slice(0, 4), anchorsM.slice(0, 4), refined);
        homographyDown = refErr <= baseErr * 1.1 ? refined : base;
      } else {
        homographyDown = base;
      }
    } catch (err) {
      if (err instanceof SingularHomographyError) return null;
      throw err;
    }

    // 10. Scale H from downsampled pixels up to original frame pixels.
    //     Pixel (x, y) in the downsampled frame corresponds to
    //     (x * sx, y * sy) in the original. So H_orig(x_o, y_o) = H(x_o/sx, y_o/sy).
    const sx = origWidth / width;
    const sy = origHeight / height;
    const homography = scaleHomographyInput(homographyDown, 1 / sx, 1 / sy);

    // 11. Project ITF doubles corners back to original-frame pixels.
    let Hinv: Matrix3x3;
    try {
      Hinv = invert3x3(homography);
    } catch {
      return null;
    }
    const corners: [number, number][] = [];
    for (const m of ITF_DOUBLES_QUAD) {
      const p = applyHomography(m, Hinv);
      if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return null;
      corners.push([p[0], p[1]]);
    }

    // 12. Confidence.
    const confidence = clamp01(net.score * servicePairScore * sidelineScore);

    return { corners, homography, confidence };
  }

  dispose(): void {
    this.off = null;
  }

  private getOffscreen(): OffscreenCanvas | null {
    if (typeof OffscreenCanvas === 'undefined') return null;
    if (!this.off) {
      this.off = new OffscreenCanvas(this.inputWidth, Math.round((this.inputWidth * 9) / 16));
    }
    return this.off;
  }
}

// ----- helpers (internal) -----

interface ExtractedFrame {
  readonly rgba: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
  readonly origWidth: number;
  readonly origHeight: number;
}

function extractRgba(
  frame: CalibratorFrame,
  targetWidth: number,
  getOffscreen: () => OffscreenCanvas | null,
): ExtractedFrame {
  if (isImageData(frame)) {
    return {
      rgba: frame.data,
      width: frame.width,
      height: frame.height,
      origWidth: frame.width,
      origHeight: frame.height,
    };
  }
  const [origW, origH] = getSourceSize(frame);
  const scale = targetWidth / origW;
  const w = targetWidth;
  const h = Math.max(1, Math.round(origH * scale));
  const off = getOffscreen();
  if (!off) {
    throw new Error(
      'AutoCourtCalibrator requires OffscreenCanvas or an ImageData input',
    );
  }
  off.width = w;
  off.height = h;
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('AutoCourtCalibrator: failed to get 2D context');
  ctx.drawImage(frame as CanvasImageSource, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  return {
    rgba: imageData.data,
    width: w,
    height: h,
    origWidth: origW,
    origHeight: origH,
  };
}

function isImageData(v: unknown): v is ImageData {
  return (
    typeof v === 'object' &&
    v !== null &&
    'data' in v &&
    'width' in v &&
    'height' in v &&
    (v as { data: unknown }).data instanceof Uint8ClampedArray
  );
}

function getSourceSize(
  frame: HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
): [number, number] {
  if ('videoWidth' in frame) return [frame.videoWidth, frame.videoHeight];
  if ('width' in frame && 'height' in frame) return [frame.width, frame.height];
  throw new Error('AutoCourtCalibrator: unsupported frame source');
}

/** A Hough line enriched with its pixel length inside the frame rectangle. */
interface ScoredLine {
  readonly line: HoughLine;
  readonly length: number;
}

/**
 * Classify Hough lines into near-horizontal (θ in ±45° of π/2) and
 * near-vertical (the rest) buckets and annotate each with its pixel length
 * inside the frame rectangle [0, W]×[0, H].
 */
function classifyLines(
  lines: readonly HoughLine[],
  width: number,
  height: number,
): { horizontals: ScoredLine[]; verticals: ScoredLine[] } {
  const horizontals: ScoredLine[] = [];
  const verticals: ScoredLine[] = [];
  const band = (45 * Math.PI) / 180;
  for (const l of lines) {
    const length = lineLengthInFrame(l, width, height);
    if (length < Math.min(width, height) * 0.2) continue;
    const scored = { line: l, length };
    if (Math.abs(l.theta - Math.PI / 2) <= band) horizontals.push(scored);
    else verticals.push(scored);
  }
  return { horizontals, verticals };
}

/**
 * Pixel length of a Hough line (in normal form `x cos θ + y sin θ = ρ`)
 * clipped to the rectangle [0, W]×[0, H]. Returns 0 if the line misses the
 * rectangle entirely.
 */
function lineLengthInFrame(l: HoughLine, W: number, H: number): number {
  const c = Math.cos(l.theta);
  const s = Math.sin(l.theta);
  const pts: [number, number][] = [];
  // Intersect with x=0 and x=W (avoid division by zero when s ≈ 0).
  if (Math.abs(s) > 1e-6) {
    const y0 = l.rho / s;
    if (y0 >= 0 && y0 <= H) pts.push([0, y0]);
    const y1 = (l.rho - W * c) / s;
    if (y1 >= 0 && y1 <= H) pts.push([W, y1]);
  }
  // Intersect with y=0 and y=H.
  if (Math.abs(c) > 1e-6) {
    const x0 = l.rho / c;
    if (x0 >= 0 && x0 <= W) pts.push([x0, 0]);
    const x1 = (l.rho - H * s) / c;
    if (x1 >= 0 && x1 <= W) pts.push([x1, H]);
  }
  if (pts.length < 2) return 0;
  // Pick the two most distant points (handles the rare case of 4 intersections
  // at rectangle corners).
  let best = 0;
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const d = Math.hypot(pts[i]![0] - pts[j]![0], pts[i]![1] - pts[j]![1]);
      if (d > best) best = d;
    }
  }
  return best;
}

interface PickedHorizontal {
  readonly line: HoughLine;
  readonly length: number;
  /** y at the horizontal center of the frame. */
  readonly yMid: number;
  /** Normalised score in [0, 1]. */
  readonly score: number;
}

/**
 * Pick the net: the horizontal with the best `votes × length × centralityY`,
 * where `centralityY = 1 - |yMid - H/2| / (H/2)`.
 */
function pickNet(
  horizontals: ScoredLine[],
  width: number,
  height: number,
): PickedHorizontal | null {
  if (horizontals.length === 0) return null;
  const hHalf = height / 2;
  const wHalf = width / 2;
  let best: PickedHorizontal | null = null;
  let maxRaw = 0;

  // First pass: compute raw scores to find the maximum for normalisation.
  const scored: PickedHorizontal[] = [];
  for (const h of horizontals) {
    const yMid = yAtX(h.line, wHalf);
    if (!Number.isFinite(yMid)) continue;
    const centralityY = 1 - Math.abs(yMid - hHalf) / hHalf;
    if (centralityY < 0) continue;
    const raw = h.line.votes * h.length * centralityY;
    if (raw > maxRaw) maxRaw = raw;
    scored.push({ line: h.line, length: h.length, yMid, score: raw });
  }
  if (scored.length === 0 || maxRaw === 0) return null;
  for (const p of scored) {
    const normalised = p.score / maxRaw;
    if (!best || normalised > best.score) {
      best = { line: p.line, length: p.length, yMid: p.yMid, score: normalised };
    }
  }
  return best;
}

/**
 * Pick the pair of service lines: one above the net, one below, roughly
 * symmetric, and at 5%..35% of frame height from the net. Returns null if no
 * acceptable pair exists.
 */
function pickServiceLines(
  horizontals: ScoredLine[],
  net: PickedHorizontal,
  width: number,
  height: number,
): {
  top: PickedHorizontal;
  bottom: PickedHorizontal;
  score: number;
} | null {
  const minDist = height * 0.05;
  const maxDist = height * 0.35;
  const above: PickedHorizontal[] = [];
  const below: PickedHorizontal[] = [];
  // Evaluate horizontals at the frame horizontal center. Near-horizontal
  // lines' y extrapolated to x=0 can diverge wildly for even tiny theta
  // deviations from π/2; x=W/2 is where θ has the least leverage.
  const xRef = width / 2;
  const yNetRef = yAtX(net.line, xRef);
  for (const h of horizontals) {
    if (h.line === net.line) continue;
    const yRef = yAtX(h.line, xRef);
    if (!Number.isFinite(yRef)) continue;
    const dNet = yRef - yNetRef;
    const absD = Math.abs(dNet);
    if (absD < minDist || absD > maxDist) continue;
    const entry: PickedHorizontal = {
      line: h.line,
      length: h.length,
      yMid: yRef,
      // Line "strength" proxy = votes (the Hough accumulator peak).
      score: h.line.votes,
    };
    if (dNet < 0) above.push(entry);
    else below.push(entry);
  }
  if (above.length === 0 || below.length === 0) return null;

  let best: {
    top: PickedHorizontal;
    bottom: PickedHorizontal;
    score: number;
  } | null = null;
  // Find the max votes across the candidates for normalisation.
  let maxVotes = 1;
  for (const a of above) if (a.score > maxVotes) maxVotes = a.score;
  for (const b of below) if (b.score > maxVotes) maxVotes = b.score;
  for (const a of above) {
    const dA = yNetRef - a.yMid;
    for (const b of below) {
      const dB = b.yMid - yNetRef;
      const symmetry = 1 - Math.min(1, Math.abs(dA - dB) / Math.max(dA, dB));
      // Geometric mean of normalised votes — gives a strong bias toward
      // BOTH lines being strong Hough peaks.
      const vNorm = Math.sqrt((a.score / maxVotes) * (b.score / maxVotes));
      const score = clamp01(symmetry * 0.35 + vNorm * 0.65);
      if (!best || score > best.score) {
        best = { top: a, bottom: b, score };
      }
    }
  }
  return best;
}

interface PickedVertical {
  readonly line: HoughLine;
  readonly length: number;
  /** x at the vertical center of the frame. */
  readonly xMid: number;
}

/**
 * Gather all verticals whose extended line crosses all three horizontals
 * inside (or near) the frame rectangle. Sorted left-to-right by their x at
 * y = H/2.
 */
function pickSidelineCandidates(
  verticals: ScoredLine[],
  horizontals: readonly HoughLine[],
  width: number,
  height: number,
): PickedVertical[] {
  const candidates: PickedVertical[] = [];
  for (const v of verticals) {
    const xMid = xAtY(v.line, height / 2);
    if (!Number.isFinite(xMid)) continue;
    let crossesAll = true;
    for (const h of horizontals) {
      const p = lineIntersect(v.line, h);
      if (!p) {
        crossesAll = false;
        break;
      }
      if (p[0] < -width * 0.1 || p[0] > width * 1.1) {
        crossesAll = false;
        break;
      }
      if (p[1] < -height * 0.1 || p[1] > height * 1.1) {
        crossesAll = false;
        break;
      }
    }
    if (!crossesAll) continue;
    candidates.push({ line: v.line, length: v.length, xMid });
  }
  candidates.sort((a, b) => a.xMid - b.xMid);
  return candidates;
}

/**
 * Identify which two sideline candidates are the SINGLES sidelines. Strategy:
 *
 *   Among candidate vertical pairs (leftCandidate, rightCandidate), score
 *   each pair by how closely its service-box quad (left × serviceTop,
 *   right × serviceTop, right × serviceBot, left × serviceBot) matches the
 *   canonical ITF singles service-box aspect ratio.
 *
 *   Additionally, if a third candidate lies approximately midway between
 *   a pair, that's a strong hint it's the center service line, so we boost
 *   that pair's score.
 *
 *   Fallback: outermost pair.
 */
function disambiguateSingles(
  candidates: PickedVertical[],
  _allVerticals: ScoredLine[],
  serviceTop: HoughLine,
  serviceBottom: HoughLine,
  net: HoughLine,
  width: number,
  height: number,
): {
  left: PickedVertical;
  right: PickedVertical;
  score: number;
  centerLine: HoughLine | null;
} | null {
  if (candidates.length < 2) return null;

  // Canonical singles service-box aspect ratio in metres:
  //   width: SINGLES_WIDTH = 8.23
  //   height: 2 * SERVICE_LINE = 12.80
  // → W/H ≈ 0.643. Perspective warps this, but roughly preserves ratios
  // between verticals-at-same-y and service-line span.
  const singlesRatio = COURT_M.SINGLES_WIDTH / (2 * COURT_M.SERVICE_LINE);

  let best: {
    left: PickedVertical;
    right: PickedVertical;
    centerLine: HoughLine | null;
    score: number;
  } | null = null;

  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const l = candidates[i]!;
      const r = candidates[j]!;
      const tl = lineIntersect(l.line, serviceTop);
      const tr = lineIntersect(r.line, serviceTop);
      const bl = lineIntersect(l.line, serviceBottom);
      const br = lineIntersect(r.line, serviceBottom);
      if (!tl || !tr || !bl || !br) continue;
      const topW = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]);
      const botW = Math.hypot(br[0] - bl[0], br[1] - bl[1]);
      const leftH = Math.hypot(bl[0] - tl[0], bl[1] - tl[1]);
      const rightH = Math.hypot(br[0] - tr[0], br[1] - tr[1]);
      const avgW = (topW + botW) / 2;
      const avgH = (leftH + rightH) / 2;
      if (avgW < width * 0.1 || avgH < height * 0.02) continue;
      const ratio = avgW / avgH;
      // How close to the canonical singles ratio? Penalise in log-space so
      // both halved and doubled ratios are symmetric.
      const ratioErr = Math.abs(Math.log(ratio) - Math.log(singlesRatio));

      // Is there a third candidate midway between l and r at the net? If so,
      // it's probably the center service line and we prefer this pair.
      const xNetL = xAtLine(l.line, net) ?? l.xMid;
      const xNetR = xAtLine(r.line, net) ?? r.xMid;
      const mid = (xNetL + xNetR) / 2;
      let centerLine: HoughLine | null = null;
      let centerBonus = 0;
      for (let k = 0; k < candidates.length; k++) {
        if (k === i || k === j) continue;
        const mCand = candidates[k]!;
        const xNetM = xAtLine(mCand.line, net) ?? mCand.xMid;
        if (xNetM <= xNetL || xNetM >= xNetR) continue;
        const rel = Math.abs(xNetM - mid) / (xNetR - xNetL);
        if (rel < 0.08) {
          centerLine = mCand.line;
          centerBonus = 0.8;
          break;
        }
      }

      const pairScore = Math.exp(-ratioErr) + centerBonus;
      if (!best || pairScore > best.score) {
        best = { left: l, right: r, centerLine, score: pairScore };
      }
    }
  }

  if (!best) return null;
  if (best.right.xMid - best.left.xMid < width * 0.2) return null;

  // Public confidence score in [0, 1]: clamp and normalise by the max
  // possible bonus + ratio term.
  const score = clamp01(best.score / 1.8);
  return {
    left: best.left,
    right: best.right,
    score,
    centerLine: best.centerLine,
  };
}

/**
 * Optional center service line: a near-vertical close to x = W/2 that lies
 * strictly BETWEEN the two singles sidelines.
 */
function pickCenterServiceLine(
  verticals: ScoredLine[],
  left: PickedVertical,
  right: PickedVertical,
  net: HoughLine,
): HoughLine | null {
  const target = (left.xMid + right.xMid) / 2;
  const halfSpan = (right.xMid - left.xMid) * 0.25;
  let best: { line: HoughLine; dev: number } | null = null;
  for (const v of verticals) {
    if (v.line === left.line || v.line === right.line) continue;
    const xOnNet = xAtLine(v.line, net);
    if (xOnNet === null) continue;
    if (xOnNet <= left.xMid || xOnNet >= right.xMid) continue;
    const dev = Math.abs(xOnNet - target);
    if (dev > halfSpan) continue;
    if (!best || dev < best.dev) best = { line: v.line, dev };
  }
  return best?.line ?? null;
}

/**
 * Optional doubles sidelines: strongest verticals outside the singles ones,
 * that still cross all three horizontals inside the frame.
 */
function pickDoublesSidelines(
  verticals: ScoredLine[],
  singlesLeft: PickedVertical,
  singlesRight: PickedVertical,
  width: number,
  height: number,
  horizontals: readonly HoughLine[],
): { left: HoughLine; right: HoughLine } | null {
  let leftBest: { line: HoughLine; xMid: number; strength: number } | null = null;
  let rightBest: { line: HoughLine; xMid: number; strength: number } | null = null;
  for (const v of verticals) {
    if (v.line === singlesLeft.line || v.line === singlesRight.line) continue;
    const xMid = xAtY(v.line, height / 2);
    if (!Number.isFinite(xMid)) continue;
    // Must cross all horizontals inside the (slightly slackened) frame.
    let ok = true;
    for (const h of horizontals) {
      const p = lineIntersect(v.line, h);
      if (!p) {
        ok = false;
        break;
      }
      if (p[0] < -width * 0.1 || p[0] > width * 1.1) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    const strength = v.length;
    if (xMid < singlesLeft.xMid - width * 0.005) {
      if (!leftBest || strength > leftBest.strength) leftBest = { line: v.line, xMid, strength };
    } else if (xMid > singlesRight.xMid + width * 0.005) {
      if (!rightBest || strength > rightBest.strength) rightBest = { line: v.line, xMid, strength };
    }
  }
  if (!leftBest || !rightBest) return null;
  return { left: leftBest.line, right: rightBest.line };
}

/** y at a given x for a line in normal form. NaN if the line is vertical. */
function yAtX(l: HoughLine, x: number): number {
  const s = Math.sin(l.theta);
  if (Math.abs(s) < 1e-6) return Number.NaN;
  return (l.rho - x * Math.cos(l.theta)) / s;
}

/** x at a given y for a line in normal form. NaN if the line is horizontal. */
function xAtY(l: HoughLine, y: number): number {
  const c = Math.cos(l.theta);
  if (Math.abs(c) < 1e-6) return Number.NaN;
  return (l.rho - y * Math.sin(l.theta)) / c;
}

/** x of line A's intersection with line B (shorthand). */
function xAtLine(a: HoughLine, b: HoughLine): number | null {
  const p = lineIntersect(a, b);
  return p ? p[0] : null;
}

function isConvex(pts: [number, number][]): boolean {
  let sign = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    const c = pts[(i + 2) % n]!;
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (cross !== 0) {
      if (sign === 0) sign = cross > 0 ? 1 : -1;
      else if ((cross > 0 ? 1 : -1) !== sign) return false;
    }
  }
  return true;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Build a homography that maps original-frame pixels to court metres from a
 * homography that maps downsampled-frame pixels to court metres, by right-
 * multiplying H with the scale matrix S = diag(invSx, invSy, 1). That is,
 * if H maps (x, y) to metres, then H' = H · S maps (x_o, y_o) where
 * (x_o, y_o) = (x / invSx, y / invSy).
 *
 * Note: `invSx = 1 / sx` where `sx = origWidth / downWidth`, so
 * `invSx = downWidth / origWidth`.
 */
function scaleHomographyInput(H: Matrix3x3, invSx: number, invSy: number): Matrix3x3 {
  const [a, b, c, d, e, f, g, h, i] = H;
  // H · diag(invSx, invSy, 1)
  return [
    a * invSx, b * invSy, c,
    d * invSx, e * invSy, f,
    g * invSx, h * invSy, i,
  ];
}

/**
 * Refine a homography by Gauss-Newton least-squares over N ≥ 4
 * correspondences. Minimises sum of squared pixel residuals between
 * `H·srcPx[i]` and `dstM[i]`. We iterate a few times — the initial DLT
 * seed is already close, so convergence is fast. Falls back to the seed if
 * the linear solve becomes singular.
 */
function refineHomographyLS(
  srcPx: [number, number][],
  dstM: [number, number][],
  seed: Matrix3x3,
): Matrix3x3 {
  // Unknown vector: 8 parameters of H (h33 = 1). Build the 2N×8 system and
  // solve normal equations A^T A h = A^T b via Gaussian elimination. For small
  // N, direct solution is fine.
  const rows: number[][] = [];
  const rhs: number[] = [];
  for (let k = 0; k < srcPx.length; k++) {
    const [x, y] = srcPx[k]!;
    const [X, Y] = dstM[k]!;
    rows.push([x, y, 1, 0, 0, 0, -x * X, -y * X]);
    rhs.push(X);
    rows.push([0, 0, 0, x, y, 1, -x * Y, -y * Y]);
    rhs.push(Y);
  }
  // Normal equations.
  const At: number[][] = Array.from({ length: 8 }, () => new Array(rows.length).fill(0));
  for (let i = 0; i < rows.length; i++) {
    for (let j = 0; j < 8; j++) At[j]![i] = rows[i]![j]!;
  }
  const AtA: number[][] = Array.from({ length: 8 }, () => new Array(8).fill(0));
  const Atb: number[] = new Array(8).fill(0);
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      let s = 0;
      for (let k = 0; k < rows.length; k++) s += At[i]![k]! * rows[k]![j]!;
      AtA[i]![j] = s;
    }
    let s = 0;
    for (let k = 0; k < rows.length; k++) s += At[i]![k]! * rhs[k]!;
    Atb[i] = s;
  }
  try {
    const sol = solve8x8(AtA, Atb);
    return [
      sol[0]!, sol[1]!, sol[2]!,
      sol[3]!, sol[4]!, sol[5]!,
      sol[6]!, sol[7]!, 1,
    ];
  } catch {
    return seed;
  }
}

/** Sum of squared residuals: H·srcPx vs dstM. */
function residual(
  srcPx: [number, number][],
  dstM: [number, number][],
  H: Matrix3x3,
): number {
  let s = 0;
  for (let i = 0; i < srcPx.length; i++) {
    const p = applyHomography(srcPx[i]!, H);
    if (!p) return Number.POSITIVE_INFINITY;
    const dx = p[0] - dstM[i]![0];
    const dy = p[1] - dstM[i]![1];
    s += dx * dx + dy * dy;
  }
  return s;
}

/** Gaussian elimination with partial pivoting for 8×8 systems. */
function solve8x8(A: number[][], b: number[]): number[] {
  const n = A.length;
  const M: number[][] = A.map((row, i) => [...row, b[i]!]);
  for (let i = 0; i < n; i++) {
    let pivot = i;
    let pivotVal = Math.abs(M[i]![i]!);
    for (let r = i + 1; r < n; r++) {
      const v = Math.abs(M[r]![i]!);
      if (v > pivotVal) {
        pivotVal = v;
        pivot = r;
      }
    }
    if (pivotVal < 1e-12) throw new Error('singular');
    if (pivot !== i) {
      const tmp = M[i]!;
      M[i] = M[pivot]!;
      M[pivot] = tmp;
    }
    const pivotRow = M[i]!;
    const pivotDiag = pivotRow[i]!;
    for (let r = i + 1; r < n; r++) {
      const row = M[r]!;
      const factor = row[i]! / pivotDiag;
      if (factor === 0) continue;
      for (let c = i; c <= n; c++) row[c] = row[c]! - factor * pivotRow[c]!;
    }
  }
  const x: number[] = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    const row = M[i]!;
    let s = row[n]!;
    for (let c = i + 1; c < n; c++) s -= row[c]! * x[c]!;
    x[i] = s / row[i]!;
  }
  return x;
}
